import { useState, useEffect, useCallback, useRef } from 'react';
import * as Y from 'yjs';
import { LocalDocBackupStore, type LocalBackupRecord } from '@/lib/persistence/LocalDocBackupStore';
import { Logger } from '@/utils/logger';
import { ShapeMapAdapter } from '@/lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '@/lib/diagram/shapeGeometry';

const logger = Logger.getLoggerWithPrefix('useLocalBackups');

interface UseLocalBackupsResult {
  backups: LocalBackupRecord[];
  isLoading: boolean;
  isRestoring: boolean;
  restore: (id: string) => Promise<void>;
  download: (id: string) => Promise<void>;
  saveManual: () => Promise<void>;
}

export function useLocalBackups(diagramId: string | undefined, doc: Y.Doc | null): UseLocalBackupsResult {
  const [backups, setBackups] = useState<LocalBackupRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRestoring, setIsRestoring] = useState(false);
  const mountedRef = useRef(true);

  const fetchBackups = useCallback(async () => {
    if (!diagramId) return;
    try {
      const list = await LocalDocBackupStore.list(diagramId);
      if (mountedRef.current) {
        setBackups(list);
        setIsLoading(false);
      }
    } catch (err) {
      logger.error('Failed to fetch backups', err);
      if (mountedRef.current) setIsLoading(false);
    }
  }, [diagramId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchBackups();
    const interval = setInterval(fetchBackups, 120_000);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchBackups]);

  const restore = useCallback(async (id: string) => {
    if (!doc || !diagramId) return;
    try {
      setIsRestoring(true);
      // Pre-restore safety backup
      await LocalDocBackupStore.save(diagramId, doc, 'pre_restore', null);

      const record = await LocalDocBackupStore.load(id);
      if (!record) {
        logger.error('Backup record not found', { id });
        return;
      }

      const update = new Uint8Array(record.snapshotBytes);
      const candidateDoc = new Y.Doc();
      Y.applyUpdate(candidateDoc, update);
      const candidateShapes = new ShapeMapAdapter(candidateDoc.getMap('shapes'));
      const candidateRecords: Record<string, unknown>[] = [];
      candidateShapes.forEach((shape, shapeId) => candidateRecords.push({ ...shape, id: shapeId }));
      new ShapePersistenceGateway(candidateShapes).validateCreates(candidateRecords);
      candidateDoc.destroy();

      // Y.applyUpdate merges (CRDT) — it can't undo operations.
      // To truly restore, clear the doc maps and apply the backup snapshot.
      doc.transact(() => {
        new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes'))).clear();
        doc.getMap('connectors').clear();
      }, 'local-restore');
      Y.applyUpdate(doc, update);
      logger.info('Restored from local backup', { id, trigger: record.trigger });
      await fetchBackups();
    } catch (err) {
      logger.error('Failed to restore from backup', err);
    } finally {
      if (mountedRef.current) setIsRestoring(false);
    }
  }, [doc, diagramId, fetchBackups]);

  const download = useCallback(async (id: string) => {
    try {
      const record = await LocalDocBackupStore.load(id);
      if (!record) return;

      const blob = new Blob([record.snapshotBytes], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diagram-${record.diagramId}-backup-${record.createdAt}.crly`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Failed to download backup', err);
    }
  }, []);

  const saveManual = useCallback(async () => {
    if (!diagramId || !doc) return;
    await LocalDocBackupStore.save(diagramId, doc, 'manual', null);
    await fetchBackups();
  }, [diagramId, doc, fetchBackups]);

  return { backups, isLoading, isRestoring, restore, download, saveManual };
}
